"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.bSpline = bSpline;
exports.bSplineDeriv = bSplineDeriv;
/**
 * Some modifications made for arcify.
 * 
 * Source:
 * https://github.com/bjnortier/dxf 
 * 
 * Copied and ported to code standard as the b-spline library is not maintained any longer.
 * 
 * Source:
 * https://github.com/thibauts/b-spline
 * Copyright (c) 2015 Thibaut Séguy <thibaut.seguy@gmail.com>
 */
function bSplineDeriv(t, degree, points, knots, weights, tiebreakDirection) {
  const n = points.length; // points count
  const d = points[0].length; // point dimensionality

  if (t < 0 || t > 1) {
    throw new Error('t out of bounds [0,1]: ' + t);
  }
  if (degree < 1) throw new Error('degree must be at least 1 (linear)');
  if (degree > n - 1) throw new Error('degree must be less than or equal to point count - 1');
  if (!weights) {
    // build weight vector of length [n]
    weights = [];
    for (let i = 0; i < n; i++) {
      weights[i] = 1;
    }
  }
  if (!knots) {
    // build knot vector of length [n + degree + 1]
    knots = [];
    for (let i = 0; i < n + degree + 1; i++) {
      knots[i] = i;
    }
  } else {
    if (knots.length !== n + degree + 1) throw new Error(`Bad knot vector length, expected ${n + degree + 1} but got ${knots.length}`);
  }
  const domain = [degree, knots.length - 1 - degree];

  // remap t to the domain where the spline is defined
  const low = knots[domain[0]];
  const high = knots[domain[1]];
  t = t * (high - low) + low;
  // Clamp to the upper &  lower bounds instead of
  // throwing an error like in the original lib
  // https://github.com/bjnortier/dxf/issues/28
  t = Math.max(t, low);
  t = Math.min(t, high);

  // find s (the spline segment) for the [t] value provided
  let s = -1;
  for (s = domain[0]; s < domain[1]; s++) {
    if (tiebreakDirection === 'forward' && t >= knots[s] && t < knots[s + 1]) {
      break;
    } else if (tiebreakDirection === 'backward' && t > knots[s] && t <= knots[s + 1]) {
      break;
    } else if (tiebreakDirection == null && t >= knots[s] && t <= knots[s + 1]) {
      break;
    }
  }
  if (s === -1) {
    throw new Error('Could not determine spline segment');
  }

  // convert points to homogeneous coordinates
  const v = [];
  for (let i = 0; i < n; i++) {
    v[i] = [];
    for (let j = 0; j < d; j++) {
      v[i][j] = points[i][j] * weights[i];
    }
    v[i][d] = weights[i];
  }

  // Which points will be considered? s, s-1, ..., s - degree 

  // Create new control points for the derivative (not all may be needed)
  const Q = [];
  for (let i = 0; i < n - 1; i++) {
    Q[i] = [];
    for (let j = 0; j < d + 1; j++) {
      Q[i][j] = degree / (knots[i + degree + 1] - knots[i + 1]) * (v[i + 1][j] - v[i][j]);
    }
  }

  // Calculate point on the spline
  // l (level) goes from 1 to the curve degree + 1
  let alpha;
  for (let l = 1; l <= degree + 1; l++) {
    // build level l of the pyramid
    for (let i = s; i > s - degree - 1 + l; i--) {
      alpha = (t - knots[i]) / (knots[i + degree + 1 - l] - knots[i]);
      // interpolate each component
      for (let j = 0; j < d + 1; j++) {
        v[i][j] = (1 - alpha) * v[i - 1][j] + alpha * v[i][j];
      }
    }
  }

  // Calculate derivative
  // Original knot vector (technically, with the first and last points removed, but we can just shift the index up by one)
  // Degree is **one less**
  // s goes up by 1
  // Control points are Q
  // l (level) goes from 1 to the curve degree + 1

  let derivKnots = knots.slice(1, knots.length - 1);
  let derivDegree = degree - 1;
  let derivS = s - 1;
  for (let l = 1; l <= derivDegree + 1; l++) {
    // build level l of the pyramid
    for (let i = derivS; i > derivS - derivDegree - 1 + l; i--) {
      alpha = (t - derivKnots[i]) / (derivKnots[i + derivDegree + 1 - l] - derivKnots[i]);
      // interpolate each component
      if (i < 0 || i > Q.length - 1) {
        throw new Error(`i is out of bounds: ${i}`);
      }
      for (let j = 0; j < d + 1; j++) {
        Q[i][j] = (1 - alpha) * Q[i - 1][j] + alpha * Q[i][j];
      }
    }
  }

  // convert back to cartesian and return
  const result = [];
  for (let i = 0; i < d; i++) {
    result[i] = v[s][i] / v[s][d];
  }
  const derivative = [];
  for (let i = 0; i < d; i++) {
    // See https://public.vrac.iastate.edu/~oliver/courses/me625/week8.pdf
    derivative[i] = (Q[derivS][i] - Q[derivS][d] * result[i]) / v[s][d];
  }
  return {
    point: result,
    derivative
  };
}

/**
 * Gets a point on a b-spline curve.
 * @param {number} t Parametric value of the point to get
 * @param {number} degree Degree of the curve
 * @param {number[][]} points Control points of the curve
 * @param {number[]} knots Knot vector of the curve
 * @param {number[]} [weights] Weight vector of the curve. Default is equal weights.
 * @returns 
 */
function bSpline(t, degree, points, knots, weights) {
  return bSplineDeriv(t, degree, points, knots, weights, null).point;
}